import React, { useState, useEffect } from 'react';
import { FiSearch, FiFilter, FiGrid, FiList, FiLayers, FiX, FiUser, FiCalendar, FiDollarSign, FiMapPin, FiMail, FiExternalLink } from 'react-icons/fi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import ProjectDetailsModal from '../components/projects/ProjectDetailsModal';
import adminProjectService from '../services/adminProjectService';
import toast from 'react-hot-toast';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // cards, stack, compact
  const [selectedTab, setSelectedTab] = useState('submitted');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasMore: false
  });
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    sortBy: 'submitted_at',
    sortOrder: 'desc'
  });

  // Fetch projects
  const fetchProjects = async (page = 1, resetData = false) => {
    setLoading(true);
    try {
      const params = {
        status: selectedTab,
        page,
        limit: pagination.limit,
        ...filters
      };

      const response = await adminProjectService.getProjects(params);
      
      if (resetData || page === 1) {
        setProjects(response.data);
      } else {
        setProjects(prev => [...prev, ...response.data]);
      }
      
      setPagination(response.pagination);
    } catch (error) {
      console.error('Fetch projects error:', error);
      toast.error(error.message || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchProjects(1, true);
  }, [selectedTab, filters]);

  // Handle search
  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle approve project
  const handleApprove = async (projectId) => {
    try {
      await adminProjectService.approveProject(projectId);
      toast.success('Project approved successfully');
      fetchProjects(1, true);
    } catch (error) {
      toast.error(error.message || 'Failed to approve project');
    }
  };

  // Handle view project details
  const handleViewDetails = async (projectId) => {
    try {
      setLoading(true);
      const response = await adminProjectService.getProjectById(projectId);
      setSelectedProject(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  // Handle reject project
  const handleReject = async (projectId) => {
    // This will be implemented with a modal for rejection reason
    const reason = prompt('Please provide a reason for rejection (minimum 10 characters):');
    
    if (!reason || reason.trim().length < 10) {
      toast.error('Rejection reason must be at least 10 characters');
      return;
    }

    try {
      await adminProjectService.rejectProject(projectId, reason);
      toast.success('Project rejected');
      setShowDetailsModal(false);
      fetchProjects(1, true);
    } catch (error) {
      toast.error(error.message || 'Failed to reject project');
    }
  };

  // Handle approve project
  const handleApproveFromModal = async (projectId) => {
    try {
      await adminProjectService.approveProject(projectId);
      toast.success('Project approved successfully');
      setShowDetailsModal(false);
      fetchProjects(1, true);
    } catch (error) {
      toast.error(error.message || 'Failed to approve project');
    }
  };

  // Load more projects (for infinite scroll)
  const loadMore = () => {
    if (pagination.hasMore && !loading) {
      fetchProjects(pagination.page + 1, false);
    }
  };

  // Status tabs
  const tabs = [
    { key: 'submitted', label: 'Submitted', count: 0 },
    { key: 'active', label: 'Active', count: 0 },
    { key: 'rejected', label: 'Rejected', count: 0 },
    { key: 'completed', label: 'Completed', count: 0 }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Management</h1>
          <p className="text-gray-600 mt-1">Review and manage submitted projects</p>
        </div>
        
        {/* View Toggle - Mobile Only */}
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg md:hidden">
          <button
            onClick={() => setViewMode('cards')}
            className={`p-2 rounded ${viewMode === 'cards' ? 'bg-white shadow' : ''}`}
            title="Card View"
          >
            <FiGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('stack')}
            className={`p-2 rounded ${viewMode === 'stack' ? 'bg-white shadow' : ''}`}
            title="Stack View"
          >
            <FiLayers className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('compact')}
            className={`p-2 rounded ${viewMode === 'compact' ? 'bg-white shadow' : ''}`}
            title="Compact View"
          >
            <FiList className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`
                whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors
                ${selectedTab === tab.key
                  ? 'border-primary-teal text-primary-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search projects..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filter */}
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-teal focus:border-transparent"
            >
              <option value="">All Categories</option>
              <option value="technology">Technology</option>
              <option value="health">Health & Medical</option>
              <option value="education">Education</option>
              <option value="environment">Environment</option>
              <option value="social">Social Impact</option>
              <option value="arts">Arts & Culture</option>
              <option value="business">Business</option>
              <option value="other">Other</option>
            </select>

            {/* Sort */}
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                handleFilterChange('sortBy', sortBy);
                handleFilterChange('sortOrder', sortOrder);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-teal focus:border-transparent"
            >
              <option value="submitted_at-desc">Newest First</option>
              <option value="submitted_at-asc">Oldest First</option>
              <option value="funding_goal-desc">Highest Goal</option>
              <option value="funding_goal-asc">Lowest Goal</option>
              <option value="title-asc">Title A-Z</option>
              <option value="title-desc">Title Z-A</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Display */}
      {loading && projects.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-teal"></div>
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FiGrid className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No projects found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedTab === 'submitted' 
                ? 'No projects are currently awaiting review.'
                : `No ${selectedTab} projects found.`
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block">
            <ProjectsTable 
              projects={projects}
              onViewDetails={handleViewDetails}
            />
          </div>

          {/* Mobile Views */}
          <div className="md:hidden">
            {viewMode === 'cards' && (
              <ProjectCardList 
                projects={projects}
                onViewDetails={handleViewDetails}
              />
            )}
            {viewMode === 'stack' && (
              <ProjectStackView 
                projects={projects}
                onViewDetails={handleViewDetails}
              />
            )}
            {viewMode === 'compact' && (
              <ProjectCompactList 
                projects={projects}
                onViewDetails={handleViewDetails}
              />
            )}
          </div>

          {/* Load More / Pagination */}
          {pagination.hasMore && (
            <div className="text-center">
              <Button
                onClick={loadMore}
                disabled={loading}
                variant="outline"
                className="w-full sm:w-auto"
              >
                {loading ? 'Loading...' : 'Load More Projects'}
              </Button>
            </div>
          )}

          {/* Pagination Info */}
          <div className="text-center text-sm text-gray-500">
            Showing {projects.length} of {pagination.total} projects
          </div>
        </>
      )}

      {/* Project Details Modal */}
      {showDetailsModal && selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedProject(null);
          }}
          onApprove={() => handleApproveFromModal(selectedProject.id)}
          onReject={() => handleReject(selectedProject.id)}
        />
      )}
    </div>
  );
};

// Desktop Table Component
const ProjectsTable = ({ projects, onViewDetails }) => (
  <Card>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owner
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Goal
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Submitted
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {projects.map((project) => {
              const formatted = adminProjectService.formatProjectForDisplay(project);
              return (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {project.title || 'Untitled Project'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {adminProjectService.getCategoryDisplayName(project.category)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatted.ownerName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatted.formattedFundingGoal}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatted.submittedDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Button
                      onClick={() => onViewDetails(project.id)}
                      size="sm"
                      className="bg-primary-teal hover:bg-primary-teal/90 text-white"
                    >
                      Review Project
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

// Mobile Card List Component
const ProjectCardList = ({ projects, onViewDetails }) => (
  <div className="space-y-4">
    {projects.map((project) => {
      const formatted = adminProjectService.formatProjectForDisplay(project);
      return (
        <Card key={project.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-medium text-gray-900 line-clamp-2">
                {project.title || 'Untitled Project'}
              </h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${formatted.statusBadgeColor}`}>
                {project.status}
              </span>
            </div>
            
            <div className="space-y-2 text-sm text-gray-600 mb-4">
              <div>by {formatted.ownerName}</div>
              <div>{adminProjectService.getCategoryDisplayName(project.category)} • {formatted.formattedFundingGoal}</div>
              <div>📅 {formatted.submittedDate}</div>
            </div>

            <Button
              onClick={() => onViewDetails(project.id)}
              size="sm"
              className="w-full bg-primary-teal hover:bg-primary-teal/90 text-white"
            >
              📋 Review Project
            </Button>
          </CardContent>
        </Card>
      );
    })}
  </div>
);

// Mobile Stack View Component
const ProjectStackView = ({ projects, onViewDetails }) => (
  <div className="text-center py-8">
    <p className="text-gray-500">Stack view coming soon...</p>
    <p className="text-sm text-gray-400 mt-2">Will implement swipe-based review interface</p>
  </div>
);

// Mobile Compact List Component
const ProjectCompactList = ({ projects, onViewDetails }) => (
  <div className="space-y-2">
    {projects.map((project) => {
      const formatted = adminProjectService.formatProjectForDisplay(project);
      return (
        <div key={project.id} className="flex items-center justify-between p-3 bg-white rounded-lg border hover:shadow-sm">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">📋</span>
              <span className="font-medium text-gray-900 truncate">
                {project.title || 'Untitled'}
              </span>
              <span className="text-sm text-gray-500">
                {formatted.formattedFundingGoal}
              </span>
            </div>
          </div>
          <Button
            onClick={() => onViewDetails(project.id)}
            size="sm"
            variant="outline"
            className="text-primary-teal border-primary-teal hover:bg-primary-teal/10"
          >
            Review
          </Button>
        </div>
      );
    })}
  </div>
);

export default ProjectsPage;
